'use client'

import { type AxiosError, type AxiosResponse } from 'axios'
import useSWRMutation, { type SWRMutationResponse } from 'swr/mutation'

import { type LoginRequestV3 } from '@redteclab/api/clients/bully'

import { buildHomeOneApiAxiosInstance } from '../../../base-api-communication'
import { type VALIDATION_ERROR_FRIENDLY_CAPTCHA_FAILED } from '../../../base-form-handling'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../global-config'
import { HTTP_HEADER_SKIP_FRIENDLY_CAPTCHA_VERIFICATION } from '../../../http'
import { urlResolverGetLoginApiEndpoint } from '../../../url-handling'
import {
  refetchUserApiSwrSessionV1Customer,
  userApiSwrRefetchMiniCart,
} from '../../../user-data'

import { userApiSwrGetCustomerPreferredLanguage } from './useApiSwrGetCustomerPreferredLanguage'

export type UserLoginResponseError = {
  error?: typeof VALIDATION_ERROR_FRIENDLY_CAPTCHA_FAILED
  status?: number
}

const postSubmitLoginData =
  (globalConfig: PublicGlobalConfig) =>
  async (
    swrKey: string,
    data: { arg: LoginRequestV3 },
  ): Promise<AxiosResponse> => {
    return buildHomeOneApiAxiosInstance(globalConfig).post(
      urlResolverGetLoginApiEndpoint(),
      data.arg,
      {
        headers: {
          ...(process.env.NEXT_PUBLIC_SKIP_FRIENDLY_CAPTCHA === 'true' && {
            [HTTP_HEADER_SKIP_FRIENDLY_CAPTCHA_VERIFICATION]: 'true',
          }),
        },
      },
    )
  }

const getUserLoginMutationKey = (): string => {
  return 'useUserApiLoginMutation'
}

/**
 * after user logged in we refetch some swrs - e.g., minicart, or session to show
 * that user is logged in on account icon
 */

export const refetchCustomerSessionSwrWhenUserLogsIn =
  async (): Promise<void> => {
    await Promise.all([
      refetchUserApiSwrSessionV1Customer(),
      userApiSwrRefetchMiniCart(),
    ])
  }

/**
 *
 */
export const refetchSwrsWhenUserLogsIn = async (): Promise<
  string | undefined
> => {
  await refetchCustomerSessionSwrWhenUserLogsIn()
  const language = await userApiSwrGetCustomerPreferredLanguage()

  return language?.preferredLanguage
}

export const useUserApiLoginMutation = ({
  onError,
  onSuccess,
}: {
  onError: () => void
  onSuccess?: (
    afterLoginSideEffectsPromise: Promise<string | undefined>,
  ) => void
}): SWRMutationResponse<
  AxiosResponse,
  AxiosError<UserLoginResponseError>,
  string,
  LoginRequestV3
> => {
  const globalConfig = useGlobalConfigContext()

  return useSWRMutation(
    getUserLoginMutationKey(),
    postSubmitLoginData(globalConfig),
    {
      onError,
      onSuccess: () => {
        onSuccess?.(refetchSwrsWhenUserLogsIn())
      },
    },
  )
}
